import { db } from './firebaseConfig';
import {
  doc,
  updateDoc,
  deleteDoc,
  collection,
  query,
  where,
  getDocs,
  getDoc,
  orderBy,
  limit,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { Message } from '../models/Message';

/**
 * @deprecated Thumbs feedback UI removed, function kept for data compatibility
 * Updates a message's feedback (thumbs up/down) in Firebase.
 *
 * @param userId - The ID of the user
 * @param conversationId - The ID of the conversation
 * @param messageId - The ID of the message to update
 * @param thinkingType - The type of conversation ('reflection' | 'fraktal' | 'prompt-graph')
 * @param feedback - The feedback object with thumbsUp/thumbsDown properties
 * @returns Promise that resolves when the update is complete
 */
export const updateMessageFeedback = async (
  userId: string,
  conversationId: string,
  messageId: string,
  thinkingType: 'reflection' | 'fraktal' | 'prompt-graph',
  feedback: { thumbsUp?: boolean; thumbsDown?: boolean }
): Promise<void> => {
  try {
    let conversationType: string;
    if (thinkingType === 'fraktal') {
      conversationType = 'fraktal_conversations';
    } else if (thinkingType === 'prompt-graph') {
      conversationType = 'graph_conversations';
    } else {
      conversationType = 'reflection_conversations';
    }
    const messagePath = `users/${userId}/${conversationType}/${conversationId}/messages/${messageId}`;
    const messageRef = doc(db, messagePath);

    // Update only the extra_parameters field
    await updateDoc(messageRef, {
      'extra_parameters.thumbsUp': feedback.thumbsUp || null,
      'extra_parameters.thumbsDown': feedback.thumbsDown || null,
      'extra_parameters.feedbackTimestamp': new Date().toISOString(),
    });

    console.log(`Successfully updated feedback for message ${messageId}`);
  } catch (error) {
    console.error(`Error updating feedback for message ${messageId}:`, error);
    throw error;
  }
};

/**
 * Deletes a message and all subsequent messages in a conversation.
 *
 * @param userId - The ID of the user
 * @param conversationId - The ID of the conversation
 * @param messageId - The ID of the message to delete
 * @param createdAt - The timestamp of the message to delete
 * @param thinkingType - The type of conversation ('reflection' or 'fraktal')
 * @returns Promise that resolves when deletion is complete
 */
/**
 * Updates conversation metadata after message deletion
 */
const updateConversationAfterMessageDeletion = async (
  userId: string,
  conversationId: string,
  deletedMessageIds: string[],
  thinkingType: 'reflection' | 'fraktal' | 'prompt-graph'
): Promise<void> => {
  try {
    let conversationType: string;
    if (thinkingType === 'fraktal') {
      conversationType = 'fraktal_conversations';
    } else if (thinkingType === 'prompt-graph') {
      conversationType = 'graph_conversations';
    } else {
      conversationType = 'reflection_conversations';
    }
    const conversationPath = `users/${userId}/${conversationType}/${conversationId}`;
    const conversationRef = doc(db, conversationPath);

    // Get the current conversation document
    const conversationDoc = await getDoc(conversationRef);
    if (!conversationDoc.exists()) {
      console.warn(`Conversation ${conversationId} does not exist`);
      return;
    }

    const conversationData = conversationDoc.data();

    // Get remaining messages to update last message info
    const messagesCollectionPath = `users/${userId}/${conversationType}/${conversationId}/messages`;
    const messagesCollection = collection(db, messagesCollectionPath);
    const remainingMessagesQuery = query(
      messagesCollection,
      orderBy('createdAt', 'desc'),
      limit(1)
    );
    const remainingMessagesSnapshot = await getDocs(remainingMessagesQuery);

    const allMessages = await getDocs(messagesCollection);

    const newMessageCount = allMessages.size;

    const updateData: any = {
      messageCount: newMessageCount,
      updatedAt: serverTimestamp(),
    };

    // Update last message info if there are remaining messages
    if (!remainingMessagesSnapshot.empty) {
      const lastMessageDoc = remainingMessagesSnapshot.docs[0];
      const lastMessageData = lastMessageDoc.data();

      updateData.lastMessageText = lastMessageData.text || lastMessageData.input_text || '';
      updateData.lastMessageTimestamp = lastMessageData.createdAt;
    } else {
      // No messages left, clear last message info
      updateData.lastMessageText = '';
      updateData.lastMessageTimestamp = conversationData.createdAt || serverTimestamp();
    }

    // Update the conversation document
    await updateDoc(conversationRef, updateData);

    console.log(`Updated conversation ${conversationId} metadata after message deletion`);
  } catch (error) {
    console.error(`Error updating conversation ${conversationId} after message deletion:`, error);
    // Don't throw - let the deletion succeed even if metadata update fails
  }
};

export const deleteMessageAndSubsequent = async (
  userId: string,
  conversationId: string,
  messageId: string,
  createdAt: string,
  thinkingType: 'reflection' | 'fraktal' | 'prompt-graph'
): Promise<void> => {
  try {
    let conversationType: string;
    if (thinkingType === 'fraktal') {
      conversationType = 'fraktal_conversations';
    } else if (thinkingType === 'prompt-graph') {
      conversationType = 'graph_conversations';
    } else {
      conversationType = 'reflection_conversations';
    }
    const messagesCollectionPath = `users/${userId}/${conversationType}/${conversationId}/messages`;
    const messagesCollection = collection(db, messagesCollectionPath);

    // Query for this message and all messages created after it
    const messagesQuery = query(
      messagesCollection,
      where('createdAt', '>=', createdAt),
      orderBy('createdAt', 'asc')
    );

    const messagesSnapshot = await getDocs(messagesQuery);

    if (messagesSnapshot.empty) {
      console.warn(`No messages found to delete with createdAt >= ${createdAt}`);
      return;
    }

    console.log(`Deleting ${messagesSnapshot.size} messages from conversation ${conversationId}`);

    // Extract document IDs from all messages being deleted
    const allDocumentIds: string[] = [];
    messagesSnapshot.docs.forEach(messageDoc => {
      const messageData = messageDoc.data();
      if (messageData.attachments && Array.isArray(messageData.attachments)) {
        messageData.attachments.forEach((attachment: any) => {
          if (attachment.type === 'document' && attachment.id) {
            allDocumentIds.push(attachment.id);
          }
        });
      }
    });

    // Mark all documents as deleted BEFORE deleting messages
    if (allDocumentIds.length > 0) {
      try {
        const { markDocumentsAsDeleted } = await import('./documentUploadService');
        await markDocumentsAsDeleted(userId, allDocumentIds);
        console.log(`[MessageService] Marked ${allDocumentIds.length} documents as deleted`);
      } catch (error) {
        console.error('[MessageService] Failed to mark documents as deleted:', error);
        // Continue with message deletion even if document marking fails
      }
    }

    // Delete each message
    const messageDeletions = messagesSnapshot.docs.map(async messageDoc => {
      try {
        const messageDocPath = `${messagesCollectionPath}/${messageDoc.id}`;
        const messageDocRef = doc(db, messageDocPath);
        await deleteDoc(messageDocRef);
        console.log(`Deleted message ${messageDoc.id}`);
      } catch (error) {
        console.error(`Error deleting message ${messageDoc.id}:`, error);
        // Continue with other deletions even if one fails
      }
    });

    // Wait for all deletions to complete
    await Promise.all(messageDeletions);

    // Update the conversation document to reflect the deleted messages
    await updateConversationAfterMessageDeletion(
      userId,
      conversationId,
      messagesSnapshot.docs.map(doc => doc.id),
      thinkingType
    );

    console.log(`Successfully deleted message ${messageId} and subsequent messages`);
  } catch (error) {
    console.error(`Error deleting message ${messageId} and subsequent messages:`, error);
    throw error;
  }
};
